import React, { useMemo, useState } from "react";
import { useChangePassword } from "../../hooks/user";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  Button,
  useTheme,
  useMediaQuery} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Autorenew as GenerateIcon} from "@mui/icons-material";
import { ModernTextField, GradientButton } from "../common/StyledComponents";
import PasswordStrengthMeter from "../common/PasswordStrengthMeter";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { isPasswordCompliant } from "../../utils/passwordPolicy";

type ChangePasswordDialogProps = {
  open: boolean;
  onClose: () => void;
};

const getCrypto = (): Crypto | null => {
  if (
    typeof window !== "undefined" &&
    typeof window.crypto !== "undefined" &&
    typeof window.crypto.getRandomValues === "function"
  ) {
    return window.crypto;
  }
  return null;
};

const generateStrongPassword = (length: number = 16): string => {
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const symbols = "@$!%*?&";
  const all = upper + lower + digits + symbols;

  // Ensure at least one of each
  const required = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
  ];

  const remainingLength = Math.max(length - required.length, 0);
  const result: string[] = [...required];

  const cryptoObj = getCrypto();
  if (cryptoObj) {
    const buf = new Uint32Array(remainingLength);
    cryptoObj.getRandomValues(buf);
    for (let i = 0; i < remainingLength; i++) {
      result.push(all[buf[i] % all.length]);
    }
  } else {
    for (let i = 0; i < remainingLength; i++) {
      result.push(all[Math.floor(Math.random() * all.length)]);
    }
  }

  // Shuffle to mix the required characters
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result.join("");
};

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { error, helpText, isLoading, clearError, handleError } = useError();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successOpen, setSuccessOpen] = useState(false);
  const [copiedOpen, setCopiedOpen] = useState(false);

  const compliant = useMemo(() => isPasswordCompliant(newPassword), [newPassword]);
  const setFieldError = (field: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
  };
  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };
  const clearAllFieldErrors = () => setFieldErrors({});

  const validate = (): boolean => {
    clearAllFieldErrors();
    let valid = true;
    if (!currentPassword.trim()) {
      setFieldError("currentPassword", "Current password is required.");
      valid = false;
    }
    if (!newPassword.trim()) {
      setFieldError("newPassword", "New password is required.");
      valid = false;
    }
    if (!confirmPassword.trim()) {
      setFieldError("confirmPassword", "Please confirm your new password.");
      valid = false;
    } else if (newPassword !== confirmPassword) {
      setFieldError("confirmPassword", "Passwords do not match.");
      valid = false;
    }
    if (currentPassword && newPassword && currentPassword === newPassword) {
      setFieldError("newPassword", "New password must be different from current password.");
      valid = false;
    }
    if (!isPasswordCompliant(newPassword)) {
      setFieldError("newPassword", "Password must meet all requirements (length, uppercase, lowercase, number, symbol).");
      valid = false;
    }
    return valid;
  };

  const { mutate: changePassword } = useChangePassword();

  const onSubmit = () => {
    clearError();
    if (!validate()) return;
    
    changePassword(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setSuccessOpen(true);
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
          onClose();
        },
        onError: (error) => {
          // Use the backend's user-friendly error message
          handleError(error);
        }
      }
    );
  };

  const onGenerate = () => {
    const generated = generateStrongPassword(16);
    setNewPassword(generated);
    // Do not auto-fill confirm; let the user confirm intentionally
    clearFieldError("newPassword");
  };

  const onCopy = async () => {
    try {
      if (!newPassword) return;
      await navigator.clipboard.writeText(newPassword);
      setCopiedOpen(true);
    } catch {
      // ignore; optional UX
    }
  };

  const onCloseDialog = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={onCloseDialog} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>Change password</DialogTitle>
      <DialogContent dividers>
        <ErrorAlert error={error} helpText={helpText} onClear={clearError} title="Unable to update password" />

        <ModernTextField
          fullWidth
          margin="normal"
          label="Current password"
          type={showCurrent ? "text" : "password"}
          value={currentPassword}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
            clearFieldError("currentPassword");
          }}
          error={!!fieldErrors.currentPassword}
          helperText={fieldErrors.currentPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="toggle current password visibility" onClick={() => setShowCurrent((s) => !s)} edge="end">
                  {showCurrent ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )}}
        />

        <ModernTextField
          fullWidth
          margin="normal"
          label="New password"
          type={showNew ? "text" : "password"}
          value={newPassword}
          onChange={(e) => {
            const v = e.target.value;
            setNewPassword(v);
            clearFieldError("newPassword");
            if (confirmPassword) {
              if (confirmPassword !== v) {
                setFieldError("confirmPassword", "Passwords do not match.");
              } else {
                clearFieldError("confirmPassword");
              }
            }
          }}
          error={!!fieldErrors.newPassword}
          helperText={fieldErrors.newPassword || "Use at least 8 characters with uppercase, lowercase, number, and symbol."}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="toggle new password visibility" onClick={() => setShowNew((s) => !s)} edge="end">
                  {showNew ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )}}
        />

        <Box sx={{ display: "flex", gap: 1, mt: 1, mb: 1 }}>
          <Button size="small" startIcon={<GenerateIcon />} onClick={onGenerate}>
            Generate strong password
          </Button>
          <Button size="small" startIcon={<CopyIcon />} onClick={onCopy} disabled={!newPassword}>
            Copy
          </Button>
        </Box>

        <PasswordStrengthMeter password={newPassword} />

        <ModernTextField
          fullWidth
          margin="normal"
          label="Confirm new password"
          type={showConfirm ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => {
            const v = e.target.value;
            setConfirmPassword(v);
            if (newPassword && v && newPassword !== v) {
              setFieldError("confirmPassword", "Passwords do not match.");
            } else {
              clearFieldError("confirmPassword");
            }
          }}
          error={!!fieldErrors.confirmPassword}
          helperText={fieldErrors.confirmPassword || "Must match your new password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="toggle confirmation password visibility" onClick={() => setShowConfirm((s) => !s)} edge="end">
                  {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )}}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCloseDialog} disabled={isLoading}>Cancel</Button>
        <GradientButton
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={onSubmit}
          disabled={isLoading || !compliant || newPassword !== confirmPassword}
        >
          {isLoading ? "Saving..." : !compliant ? "Meet password requirements" : newPassword !== confirmPassword ? "Passwords must match" : "Update password"}
        </GradientButton>
      </DialogActions>

      <Snackbar open={successOpen} autoHideDuration={4000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSuccessOpen(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Your password has been updated successfully.
        </Alert>
      </Snackbar>

      <Snackbar open={copiedOpen} autoHideDuration={2000} onClose={() => setCopiedOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setCopiedOpen(false)} severity="info" variant="filled" sx={{ width: "100%" }}>
          Password copied to clipboard.
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ChangePasswordDialog;


